import React, { useState } from 'react';
import type { PlanCatalogUpdate } from '../../api/modules/admin';

interface EditablePlan {
  id: string;
  name: string;
  displayName?: string;
  priceMonthly: number | string;
  maxSites: number;
  maxPagesPerSite: number;
  allowCustomDomain?: boolean;
  allowContactForms?: boolean;
  allowAdvancedComponents?: boolean;
  allowCompeteStudio?: boolean;
  customDomain?: boolean;
  contactForms?: boolean;
  advancedComponents?: boolean;
  competeStudio?: boolean;
  features?: string[];
}

interface EditPlanModalProps {
  plan: EditablePlan;
  onSave: (planId: string, data: PlanCatalogUpdate) => Promise<void>;
  onCancel: () => void;
}

function flag(
  plan: EditablePlan,
  apiName: keyof EditablePlan,
  legacyName: keyof EditablePlan
): boolean {
  if (typeof plan[apiName] === 'boolean') return plan[apiName] as boolean;
  return Boolean(plan[legacyName]);
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Failed to save this plan';
}

export const EditPlanModal: React.FC<EditPlanModalProps> = ({ plan, onSave, onCancel }) => {
  const isFree = plan.name.toUpperCase() === 'FREE';
  const [displayName, setDisplayName] = useState(plan.displayName || plan.name);
  const [priceMonthly, setPriceMonthly] = useState(String(plan.priceMonthly ?? ''));
  const [sitesUnlimited, setSitesUnlimited] = useState(plan.maxSites === -1);
  const [maxSites, setMaxSites] = useState(plan.maxSites === -1 ? '1' : String(plan.maxSites));
  const [pagesUnlimited, setPagesUnlimited] = useState(plan.maxPagesPerSite === -1);
  const [maxPages, setMaxPages] = useState(
    plan.maxPagesPerSite === -1 ? '1' : String(plan.maxPagesPerSite)
  );
  const [allowCustomDomain, setAllowCustomDomain] = useState(
    flag(plan, 'allowCustomDomain', 'customDomain')
  );
  const [allowContactForms, setAllowContactForms] = useState(
    flag(plan, 'allowContactForms', 'contactForms')
  );
  const [allowAdvancedComponents, setAllowAdvancedComponents] = useState(
    flag(plan, 'allowAdvancedComponents', 'advancedComponents')
  );
  const [allowCompeteStudio, setAllowCompeteStudio] = useState(
    flag(plan, 'allowCompeteStudio', 'competeStudio')
  );
  const [featuresText, setFeaturesText] = useState((plan.features ?? []).join('\n'));
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedName = displayName.trim();
    const price = Number(priceMonthly);
    const sites = sitesUnlimited ? -1 : Number(maxSites);
    const pages = pagesUnlimited ? -1 : Number(maxPages);

    if (!trimmedName) {
      setError('Display name is required');
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      setError('Price must be zero or greater');
      return;
    }
    if (!sitesUnlimited && (!Number.isInteger(sites) || sites < 1)) {
      setError('Max sites must be a whole number of at least 1');
      return;
    }
    if (!pagesUnlimited && (!Number.isInteger(pages) || pages < 1)) {
      setError('Max pages must be a whole number of at least 1');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      await onSave(plan.id, {
        displayName: trimmedName,
        priceMonthly: price,
        maxSites: sites,
        maxPagesPerSite: pages,
        allowCustomDomain,
        allowContactForms,
        allowAdvancedComponents,
        allowCompeteStudio,
        features: featuresText
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean),
      });
    } catch (saveError) {
      setError(errorMessage(saveError));
      setIsSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={event => {
        if (event.target === event.currentTarget && !isSaving) onCancel();
      }}
    >
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit {plan.name}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Limits and features apply right away. A new price is used for future checkouts. People
              already subscribed keep their current Stripe price. Yearly billing is 10× the monthly
              price.
            </p>
          </div>

          {error && <p className="text-sm font-medium text-red-600">{error}</p>}

          <div>
            <label
              htmlFor="plan-display-name"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Display name
            </label>
            <input
              id="plan-display-name"
              className="input-field"
              value={displayName}
              onChange={event => setDisplayName(event.target.value)}
              disabled={isSaving}
            />
          </div>

          <div>
            <label htmlFor="plan-price" className="block text-sm font-semibold text-gray-700 mb-2">
              Monthly price (USD)
            </label>
            <input
              id="plan-price"
              className="input-field"
              type="number"
              min="0"
              step="0.01"
              value={priceMonthly}
              onChange={event => setPriceMonthly(event.target.value)}
              disabled={isSaving || isFree}
            />
          </div>

          <LimitField
            id="plan-max-sites"
            label="Max sites"
            unlimited={sitesUnlimited}
            value={maxSites}
            disabled={isSaving}
            onUnlimitedChange={setSitesUnlimited}
            onValueChange={setMaxSites}
          />
          <LimitField
            id="plan-max-pages"
            label="Max pages per site"
            unlimited={pagesUnlimited}
            value={maxPages}
            disabled={isSaving}
            onUnlimitedChange={setPagesUnlimited}
            onValueChange={setMaxPages}
          />

          <fieldset className="space-y-2">
            <legend className="text-sm font-semibold text-gray-700">Included features</legend>
            <Flag
              label="Custom domain"
              checked={allowCustomDomain}
              disabled={isSaving}
              onChange={setAllowCustomDomain}
            />
            <Flag
              label="Contact forms"
              checked={allowContactForms}
              disabled={isSaving}
              onChange={setAllowContactForms}
            />
            <Flag
              label="Advanced components"
              checked={allowAdvancedComponents}
              disabled={isSaving}
              onChange={setAllowAdvancedComponents}
            />
            <Flag
              label="Compete Studio"
              checked={allowCompeteStudio}
              disabled={isSaving}
              onChange={setAllowCompeteStudio}
            />
          </fieldset>

          <div>
            <label
              htmlFor="plan-features"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Pricing page bullets
            </label>
            <textarea
              id="plan-features"
              className="input-field resize-none"
              rows={4}
              value={featuresText}
              onChange={event => setFeaturesText(event.target.value)}
              disabled={isSaving}
              placeholder="One feature per line"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={isSaving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

function LimitField({
  id,
  label,
  unlimited,
  value,
  disabled,
  onUnlimitedChange,
  onValueChange,
}: {
  id: string;
  label: string;
  unlimited: boolean;
  value: string;
  disabled: boolean;
  onUnlimitedChange: (unlimited: boolean) => void;
  onValueChange: (value: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-2">
        {label}
      </label>
      <div className="flex items-center gap-3">
        <input
          id={id}
          className="input-field"
          type="number"
          min="1"
          step="1"
          value={unlimited ? '' : value}
          onChange={event => onValueChange(event.target.value)}
          disabled={disabled || unlimited}
        />
        <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
          <input
            type="checkbox"
            checked={unlimited}
            onChange={event => onUnlimitedChange(event.target.checked)}
            disabled={disabled}
          />
          Unlimited
        </label>
      </div>
    </div>
  );
}

function Flag({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-gray-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={event => onChange(event.target.checked)}
        disabled={disabled}
      />
      {label}
    </label>
  );
}
